import { forwardRef } from 'react';
import {
  Box,
  Heading,
  SkeletonText,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { Colors, Icon } from '@mercator-gcp/grout';
import { TabsList } from 'components/elements/TabsList';
import { FlexRow } from 'components/layout/FlexRow';
import { Panel } from 'components/layout/Panel';
import LandTitleMaterials from 'components/widgets/LandTitleSection/LandTitleMaterials';
import TitleSummaryTable from 'components/widgets/LandTitleSection/TitleSummaryTable';
import { LandTitle } from 'types/LandTitle';

type TLandTitleSection = {
  landTitleHistory: LandTitle[] | undefined;
  landTitlesLoading: boolean;
  tabIndex: number;
  setTabIndex: (index: number) => void;
};

const titleRow = {
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '148%',
  letterSpacing: '-0.01em',
};

const panelParentStyle = {
  paddingTop: 'padding_16',
};

const panelStyle = {
  padding: 0,
  paddingBottom: 'padding_8',
};

const fullWidth = {
  width: '100%',
};

const LandTitleSection = forwardRef<HTMLDivElement, TLandTitleSection>(
  ({ landTitleHistory, tabIndex, setTabIndex, landTitlesLoading }, ref) => {
    const unitOwnersCount = landTitleHistory ? landTitleHistory.length : 0;

    const HEADINGS = ['Latest Title Change'];
    const TITLES = ['Latest Title Change'];

    if (unitOwnersCount > 0) {
      HEADINGS.push(`${unitOwnersCount} Unit Owners`);
      TITLES.push(`${unitOwnersCount} Unit Owners at this address`);
    }

    const handleTabsChange = (index: number) => {
      setTabIndex(index);
    };

    return (
      <Box sx={fullWidth} ref={ref}>
        <Panel title="Land Title" icon={<Icon name={Icon.Name.LandTitle} />}>
          <SkeletonText isLoaded={!landTitlesLoading}>
            {landTitleHistory && landTitleHistory.length > 0 ? (
              <Tabs variant="enclosed" index={tabIndex} onChange={handleTabsChange}>
                <FlexRow>
                  <FlexRow.Left>
                    <Heading sx={titleRow}>{TITLES[tabIndex]}</Heading>
                  </FlexRow.Left>
                  <FlexRow.Right>
                    <TabsList className="hide-on-print" tabLabels={HEADINGS} />
                  </FlexRow.Right>
                </FlexRow>

                <TabPanels sx={panelParentStyle}>
                  <TabPanel sx={panelStyle}>
                    <LandTitleMaterials result={landTitleHistory?.[0]} skeleton={Text} />
                  </TabPanel>
                  <TabPanel sx={panelStyle}>
                    <TitleSummaryTable landTitleHistory={landTitleHistory} />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            ) : (
              <Text color={Colors.TextQuiet}>
                No land title data available at this address
              </Text>
            )}
          </SkeletonText>
        </Panel>
      </Box>
    );
  },
);

export default LandTitleSection;
